import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ValidationService } from '@lbmx/phoenix-lib-utils';
import { TranslocoService } from '@ngneat/transloco';
import * as i18nIsoCountries from 'i18n-iso-countries';
import {
  AsYouType,
  CountryCode,
  isValidNumberForRegion,
  parsePhoneNumber,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import {
  IOTPResponse,
  IUserProfileForm,
} from 'src/app/dtos/userProfile/userProfile.dto';
import {
  INotificationRequest,
  IUserNotification,
  NotificationTypes,
} from 'src/app/models/user.model';
@Component({
  selector: 'app-userprofile',
  templateUrl: './userprofile.component.html',
  styleUrls: ['./userprofile.component.scss'],
})
export class UserprofileComponent {
  @Input('defaultCountryCode') set defaultCountryCodeSetter(
    defaultCountryCode: string
  ) {
    this.defaultCountryCode = defaultCountryCode;
  }
  @Input('countries') set countriesSetter(countries: any[]) {
    this.countries = countries;
  }
  @Input('userProfileDetails') set userProfileDetailsSetter(
    userProfileDetails: IUserProfileForm
  ) {
    this.userProfileDetails = userProfileDetails;
    this.initializeForm();
    this.formatPhoneToInternational();
  }
  @Input('userNotifications') set userNotificationsSetter(
    userNotifications: IUserNotification[]
  ) {
    this.userNotifications = userNotifications;
    this.initializeNotificationForm();
  }
  @Input('otpToggleResponse') set otpToggleResponseSetter(
    containerResponse: IOTPResponse
  ) {
    this.otpToggleResponse = containerResponse;
  }
  @Input() public DTFormat;
  @Input() public fetching = false;
  @Input() public languageTypes;
  @Input() public timeZoneTypes;
  @Output() public updatedProfileInfo = new EventEmitter<any>();
  @Output() public updateUserNotifications = new EventEmitter<any>();
  @Output() public otpToggleEmitter = new EventEmitter<any>();
  @Output() public passwordConfirmationEmitter = new EventEmitter<any>();
  private otpToggleResponse: IOTPResponse;
  public userProfileForm: FormGroup;
  public userNotificationsForm: FormGroup;
  public loading = false;
  public phoneForm: FormGroup;
  public showPasswordEdit = false;
  public passwordEmpty = true;
  public countries = [];
  public defaultCountryCode = 'CAN';
  public message = {
    confirmPassword: () =>
      this.translocoService.translate('COMMON.PASSWORD_MUST_MATCH'),
  };
  public extension = {
    extension: () => 'Extension must be digits (maximum of 10 digits)',
  };
  public userProfileDetails: IUserProfileForm;
  public userNotifications: IUserNotification[];
  public phoneNumber = new AsYouType();
  public activateUserInfoTab = true;
  public defaultInterval = 24;
  constructor(
    private formBuilder: FormBuilder,
    private validationService: ValidationService,
    private translocoService: TranslocoService
  ) {}

  /*Handling Password Checkbox  Click */
  public handleUpdatePassword(status) {
    this.showPasswordEdit =
      status.checked && status.checked === true ? true : false;
  }

  private initializeForm() {
    const userProfileDetails: IUserProfileForm =
      this.userProfileDetails ||
      ({
        firstName: '',
        lastName: '',
        userName: '',
        OTPRequired: null,
        phoneCountry: 'CA',
        phone: '',
        phoneExtension: '',
        languageCode: '',
        timeZoneCode: '',
        dateTypeKey: 1,
      } as IUserProfileForm);
    this.userProfileForm = this.formBuilder.group(
      {
        firstName: [userProfileDetails.firstName, [Validators.required]],
        lastName: [userProfileDetails.lastName, [Validators.required]],
        userName: [
          userProfileDetails.userName,
          [Validators.required, Validators.email],
        ],
        OTPRequired: userProfileDetails.OTPRequired || false,
        password: [
          '',
          Validators.compose([
            this.validationService.patternValidator('number'),
            this.validationService.patternValidator('upperCase'),
            this.validationService.patternValidator('lowerCase'),
            this.validationService.specialCharacterValidator(),
            this.validationService.minimumLength(8),
          ]),
        ],
        confirmPassword: [''],
        languageCode: [userProfileDetails.languageCode, [Validators.required]],
        timeZoneCode: [userProfileDetails.timeZoneCode, [Validators.required]],
        dateTypeKey: [userProfileDetails.dateTypeKey, [Validators.required]],
      },
      {
        validator: this.validationService.MustMatch(
          'password',
          'confirmPassword'
        ),
      }
    );
    this.phoneForm = this.formBuilder.group(
      {
        phoneCountry: [this.formatCountryCodeForDropdown()],
        phoneExtension: [
          `${userProfileDetails?.phoneExtension || ''}`,
          Validators.compose([
            Validators.pattern('^[0-9]*$'),
            Validators.maxLength(10),
          ]),
        ],
        phone: [
          `${userProfileDetails?.phone}`,
          Validators.compose([
            Validators.required,
            this.validatePhoneNumber(parsePhoneNumberWithError),
          ]),
        ],
      },
      {
        validators: this.validatePhoneForm('phone', 'phoneExtension'),
      }
    );
  }

  public initializeNotificationForm() {
    if (this.userNotifications == null) {
      return;
    }
    this.userNotificationsForm = this.formBuilder.group({
      poaReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.POA) ===
          null || !this.getFieldValue('isActive', NotificationTypes.POA)
          ? false
          : true,
      ],
      poaInterval: [
        {
          value:
            this.getFieldValue('userNotificationKey', NotificationTypes.POA) ===
              null ||
            this.getFieldValue('interval', NotificationTypes.POA) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.POA),
          disabled:
            this.getFieldValue('userNotificationKey', NotificationTypes.POA) ===
              null || !this.getFieldValue('isActive', NotificationTypes.POA)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('poaReceived').value
          ),
        ],
      ],
      poReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.PO) ===
          null || !this.getFieldValue('isActive', NotificationTypes.PO)
          ? false
          : true,
      ],
      poInterval: [
        {
          value:
            this.getFieldValue('userNotificationKey', NotificationTypes.PO) ===
              null || this.getFieldValue('interval', NotificationTypes.PO) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.PO),
          disabled:
            this.getFieldValue('userNotificationKey', NotificationTypes.PO) ===
              null || !this.getFieldValue('isActive', NotificationTypes.PO)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('poReceived').value
          ),
        ],
      ],
      invoiceReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.Invoice) ===
          null || !this.getFieldValue('isActive', NotificationTypes.Invoice)
          ? false
          : true,
      ],
      invoiceInterval: [
        {
          value:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.Invoice
            ) === null ||
            this.getFieldValue('interval', NotificationTypes.Invoice) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.Invoice),
          disabled:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.Invoice
            ) === null ||
            !this.getFieldValue('isActive', NotificationTypes.Invoice)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('invoiceReceived').value
          ),
        ],
      ],
      asnReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.ASN) ===
          null || !this.getFieldValue('isActive', NotificationTypes.ASN)
          ? false
          : true,
      ],
      asnInterval: [
        {
          value:
            this.getFieldValue('userNotificationKey', NotificationTypes.ASN) ===
              null ||
            this.getFieldValue('interval', NotificationTypes.ASN) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.ASN),
          disabled:
            this.getFieldValue('userNotificationKey', NotificationTypes.ASN) ===
              null || !this.getFieldValue('isActive', NotificationTypes.ASN)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('asnReceived').value
          ),
        ],
      ],
      collectionBatch: [
        this.getFieldValue(
          'userNotificationKey',
          NotificationTypes.CollectionBatch
        ) === null ||
        !this.getFieldValue('isActive', NotificationTypes.CollectionBatch)
          ? false
          : true,
      ],
      collectionBatchInterval: [
        {
          value:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.CollectionBatch
            ) === null ||
            this.getFieldValue(
              'interval',
              NotificationTypes.CollectionBatch
            ) === 0
              ? null
              : this.getFieldValue(
                  'interval',
                  NotificationTypes.CollectionBatch
                ),
          disabled:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.CollectionBatch
            ) === null ||
            !this.getFieldValue('isActive', NotificationTypes.CollectionBatch)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('collectionBatch').value
          ),
        ],
      ],
      invoiceKeptOnHold: [
        this.getFieldValue(
          'userNotificationKey',
          NotificationTypes.InvoiceKeptOnHold
        ) === null ||
        !this.getFieldValue('isActive', NotificationTypes.InvoiceKeptOnHold)
          ? false
          : true,
      ],
      invoiceKeptOnHoldInterval: [
        {
          value:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.InvoiceKeptOnHold
            ) === null ||
            this.getFieldValue(
              'interval',
              NotificationTypes.InvoiceKeptOnHold
            ) === 0
              ? null
              : this.getFieldValue(
                  'interval',
                  NotificationTypes.InvoiceKeptOnHold
                ),
          disabled:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.InvoiceKeptOnHold
            ) === null ||
            !this.getFieldValue('isActive', NotificationTypes.InvoiceKeptOnHold)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('invoiceKeptOnHold').value
          ),
        ],
      ],
      invoiceMissing: [
        this.getFieldValue(
          'userNotificationKey',
          NotificationTypes.InvoiceMissing
        ) === null ||
        !this.getFieldValue('isActive', NotificationTypes.InvoiceMissing)
          ? false
          : true,
      ],
      invoiceMissingInterval: [
        {
          value:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.InvoiceMissing
            ) === null ||
            this.getFieldValue('interval', NotificationTypes.InvoiceMissing) ===
              0
              ? null
              : this.getFieldValue(
                  'interval',
                  NotificationTypes.InvoiceMissing
                ),
          disabled:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.InvoiceMissing
            ) === null ||
            !this.getFieldValue('isActive', NotificationTypes.InvoiceMissing)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('invoiceMissing').value
          ),
        ],
      ],
      remittanceReceived: [
        this.getFieldValue(
          'userNotificationKey',
          NotificationTypes.RemittancesReceived
        ) === null ||
        !this.getFieldValue('isActive', NotificationTypes.RemittancesReceived)
          ? false
          : true,
      ],
      remittanceInterval: [
        {
          value:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.RemittancesReceived
            ) === null ||
            this.getFieldValue(
              'interval',
              NotificationTypes.RemittancesReceived
            ) === 0
              ? null
              : this.getFieldValue(
                  'interval',
                  NotificationTypes.RemittancesReceived
                ),
          disabled:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.RemittancesReceived
            ) === null ||
            !this.getFieldValue(
              'isActive',
              NotificationTypes.RemittancesReceived
            )
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('remittanceReceived').value
          ),
        ],
      ],
      padReceived: [
        this.getFieldValue(
          'userNotificationKey',
          NotificationTypes.ReceivedPAD
        ) === null ||
        !this.getFieldValue('isActive', NotificationTypes.ReceivedPAD)
          ? false
          : true,
      ],
      padInterval: [
        {
          value:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.ReceivedPAD
            ) === null ||
            this.getFieldValue('interval', NotificationTypes.ReceivedPAD) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.ReceivedPAD),
          disabled:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.ReceivedPAD
            ) === null ||
            !this.getFieldValue('isActive', NotificationTypes.ReceivedPAD)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('padReceived').value
          ),
        ],
      ],
      osReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.OS) ===
          null || !this.getFieldValue('isActive', NotificationTypes.OS)
          ? false
          : true,
      ],
      osInterval: [
        {
          value:
            this.getFieldValue('userNotificationKey', NotificationTypes.OS) ===
              null || this.getFieldValue('interval', NotificationTypes.OS) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.OS),
          disabled:
            this.getFieldValue('userNotificationKey', NotificationTypes.OS) ===
              null || !this.getFieldValue('isActive', NotificationTypes.OS)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('osReceived').value
          ),
        ],
      ],
      pocReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.POC) ===
          null || !this.getFieldValue('isActive', NotificationTypes.POC)
          ? false
          : true,
      ],
      pocInterval: [
        {
          value:
            this.getFieldValue('userNotificationKey', NotificationTypes.POC) ===
              null ||
            this.getFieldValue('interval', NotificationTypes.POC) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.POC),
          disabled:
            this.getFieldValue('userNotificationKey', NotificationTypes.POC) ===
              null || !this.getFieldValue('isActive', NotificationTypes.POC)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('pocReceived').value
          ),
        ],
      ],
      aaReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.AA) ===
          null || !this.getFieldValue('isActive', NotificationTypes.AA)
          ? false
          : true,
      ],
      aaInterval: [
        {
          value:
            this.getFieldValue('userNotificationKey', NotificationTypes.AA) ===
              null || this.getFieldValue('interval', NotificationTypes.AA) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.AA),
          disabled:
            this.getFieldValue('userNotificationKey', NotificationTypes.AA) ===
              null || !this.getFieldValue('isActive', NotificationTypes.AA)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('aaReceived').value
          ),
        ],
      ],
      tmReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.TM) ===
          null || !this.getFieldValue('isActive', NotificationTypes.TM)
          ? false
          : true,
      ],
      tmInterval: [
        {
          value:
            this.getFieldValue('userNotificationKey', NotificationTypes.TM) ===
              null || this.getFieldValue('interval', NotificationTypes.TM) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.TM),
          disabled:
            this.getFieldValue('userNotificationKey', NotificationTypes.TM) ===
              null || !this.getFieldValue('isActive', NotificationTypes.TM)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('tmReceived').value
          ),
        ],
      ],
      statementReceived: [
        this.getFieldValue(
          'userNotificationKey',
          NotificationTypes.STATEMENT
        ) === null ||
        !this.getFieldValue('isActive', NotificationTypes.STATEMENT)
          ? false
          : true,
      ],
      statementInterval: [
        {
          value:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.STATEMENT
            ) === null ||
            this.getFieldValue('interval', NotificationTypes.STATEMENT) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.STATEMENT),
          disabled:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.STATEMENT
            ) === null ||
            !this.getFieldValue('isActive', NotificationTypes.STATEMENT)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('statementReceived').value
          ),
        ],
      ],
      rebateStatementReceived: [
        this.getFieldValue(
          'userNotificationKey',
          NotificationTypes.REBATESTATEMENT
        ) === null ||
        !this.getFieldValue('isActive', NotificationTypes.REBATESTATEMENT)
          ? false
          : true,
      ],
      rebateStatementInterval: [
        {
          value:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.REBATESTATEMENT
            ) === null ||
            this.getFieldValue(
              'interval',
              NotificationTypes.REBATESTATEMENT
            ) === 0
              ? null
              : this.getFieldValue(
                  'interval',
                  NotificationTypes.REBATESTATEMENT
                ),
          disabled:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.REBATESTATEMENT
            ) === null ||
            !this.getFieldValue('isActive', NotificationTypes.REBATESTATEMENT)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () =>
              this.userNotificationsForm.get('rebateStatementReceived').value
          ),
        ],
      ],
      cdaReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.CDA) ===
          null || !this.getFieldValue('isActive', NotificationTypes.CDA)
          ? false
          : true,
      ],
      cdaInterval: [
        {
          value:
            this.getFieldValue('userNotificationKey', NotificationTypes.CDA) ===
              null ||
            this.getFieldValue('interval', NotificationTypes.CDA) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.CDA),
          disabled:
            this.getFieldValue('userNotificationKey', NotificationTypes.CDA) ===
              null || !this.getFieldValue('isActive', NotificationTypes.CDA)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('cdaReceived').value
          ),
        ],
      ],
      dsdsiReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.DSDSI) ===
          null || !this.getFieldValue('isActive', NotificationTypes.DSDSI)
          ? false
          : true,
      ],
      dsdsiInterval: [
        {
          value:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.DSDSI
            ) === null ||
            this.getFieldValue('interval', NotificationTypes.DSDSI) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.DSDSI),
          disabled:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.DSDSI
            ) === null ||
            !this.getFieldValue('isActive', NotificationTypes.DSDSI)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('dsdsiReceived').value
          ),
        ],
      ],
      psrcReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.PSRC) ===
          null || !this.getFieldValue('isActive', NotificationTypes.PSRC)
          ? false
          : true,
      ],
      psrcInterval: [
        {
          value:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.PSRC
            ) === null ||
            this.getFieldValue('interval', NotificationTypes.PSRC) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.PSRC),
          disabled:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.PSRC
            ) === null ||
            !this.getFieldValue('isActive', NotificationTypes.PSRC)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('psrcReceived').value
          ),
        ],
      ],
      piReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.PI) ===
          null || !this.getFieldValue('isActive', NotificationTypes.PI)
          ? false
          : true,
      ],
      piInterval: [
        {
          value:
            this.getFieldValue('userNotificationKey', NotificationTypes.PI) ===
              null || this.getFieldValue('interval', NotificationTypes.PI) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.PI),
          disabled:
            this.getFieldValue('userNotificationKey', NotificationTypes.PI) ===
              null || !this.getFieldValue('isActive', NotificationTypes.PI)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('piReceived').value
          ),
        ],
      ],
      wsoReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.WSO) ===
          null || !this.getFieldValue('isActive', NotificationTypes.WSO)
          ? false
          : true,
      ],
      wsoInterval: [
        {
          value:
            this.getFieldValue('userNotificationKey', NotificationTypes.WSO) ===
              null ||
            this.getFieldValue('interval', NotificationTypes.WSO) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.WSO),
          disabled:
            this.getFieldValue('userNotificationKey', NotificationTypes.WSO) ===
              null || !this.getFieldValue('isActive', NotificationTypes.WSO)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('wsoReceived').value
          ),
        ],
      ],
      wstsaReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.WSTSA) ===
          null || !this.getFieldValue('isActive', NotificationTypes.WSTSA)
          ? false
          : true,
      ],
      wstsaInterval: [
        {
          value:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.WSTSA
            ) === null ||
            this.getFieldValue('interval', NotificationTypes.WSTSA) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.WSTSA),
          disabled:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.WSTSA
            ) === null ||
            !this.getFieldValue('isActive', NotificationTypes.WSTSA)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('wstsaReceived').value
          ),
        ],
      ],
      wstraReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.WSTRA) ===
          null || !this.getFieldValue('isActive', NotificationTypes.WSTRA)
          ? false
          : true,
      ],
      wstraInterval: [
        {
          value:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.WSTRA
            ) === null ||
            this.getFieldValue('interval', NotificationTypes.WSTRA) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.WSTRA),
          disabled:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.WSTRA
            ) === null ||
            !this.getFieldValue('isActive', NotificationTypes.WSTRA)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('wstraReceived').value
          ),
        ],
      ],
      wsaReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.WSA) ===
          null || !this.getFieldValue('isActive', NotificationTypes.WSA)
          ? false
          : true,
      ],
      wsaInterval: [
        {
          value:
            this.getFieldValue('userNotificationKey', NotificationTypes.WSA) ===
              null ||
            this.getFieldValue('interval', NotificationTypes.WSA) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.WSA),
          disabled:
            this.getFieldValue('userNotificationKey', NotificationTypes.WSA) ===
              null || !this.getFieldValue('isActive', NotificationTypes.WSA)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('wsaReceived').value
          ),
        ],
      ],
      wiaaReceived: [
        this.getFieldValue('userNotificationKey', NotificationTypes.WIAA) ===
          null || !this.getFieldValue('isActive', NotificationTypes.WIAA)
          ? false
          : true,
      ],
      wiaaInterval: [
        {
          value:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.WIAA
            ) === null ||
            this.getFieldValue('interval', NotificationTypes.WIAA) === 0
              ? null
              : this.getFieldValue('interval', NotificationTypes.WIAA),
          disabled:
            this.getFieldValue(
              'userNotificationKey',
              NotificationTypes.WIAA
            ) === null ||
            !this.getFieldValue('isActive', NotificationTypes.WIAA)
              ? true
              : false,
        },
        [
          Validators.pattern('^[0-9]+(.[0-9]{0,9})?$'),
          this.requiredIfValidator(
            () => this.userNotificationsForm.get('wiaaReceived').value
          ),
        ],
      ],
    });

    this.loading = true;

    this.userNotifications?.map((notifn) => {
      const valueChanges = this.userNotificationsForm?.controls[
        `'${notifn.checkboxCtl}`
      ]
        ? this.userNotificationsForm
            .get(notifn.checkboxCtl)
            .valueChanges.subscribe((value) => {
              this.userNotificationsForm
                ?.get(notifn.intervalCtl)
                ?.updateValueAndValidity({ emitEvent: false });
            })
        : '';
    });
  }

  private getFieldValue(field, value) {
    let data;
    switch (field) {
      case 'notificationTypeKey':
        data =
          this.userNotifications.filter((e) => {
            return e.notificationTypeKey === value;
          }).length > 0;

        break;
      case 'userNotificationKey':
        const notifn = this.userNotifications.find(
          (n) => n.notificationTypeKey === value
        );
        data = notifn.userNotificationKey;

        break;
      case 'interval':
        const userNotifns = this.userNotifications.find(
          (n) => n.notificationTypeKey === value
        );
        data = userNotifns.interval;
        break;
      case 'isActive':
        const userNotifn = this.userNotifications.find(
          (n) => n.notificationTypeKey === value
        );
        data = userNotifn.isActive;
        break;
      default:
        break;
    }
    return data;
  }
  public checkBoxStatus(event) {
    switch (event) {
      case NotificationTypes.POA:
        if (!this.userNotificationsForm.value['poaReceived']) {
          this.userNotificationsForm.controls.poaInterval.setValue(null);
          this.userNotificationsForm.get('poaInterval').disable();
        } else {
          this.userNotificationsForm.get('poaInterval').enable();
          this.userNotificationsForm.controls.poaInterval.setValue(
            this.defaultInterval
          );
        }
        break;
      case NotificationTypes.PO:
        if (!this.userNotificationsForm.value['poReceived']) {
          this.userNotificationsForm.controls.poInterval.setValue(null);
          this.userNotificationsForm.get('poInterval').disable();
        } else {
          this.userNotificationsForm.controls.poInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('poInterval').enable();
        }
        break;
      case NotificationTypes.Invoice:
        if (!this.userNotificationsForm.value['invoiceReceived']) {
          this.userNotificationsForm.controls.invoiceInterval.setValue(null);
          this.userNotificationsForm.get('invoiceInterval').disable();
        } else {
          this.userNotificationsForm.controls.invoiceInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('invoiceInterval').enable();
        }
        break;
      case NotificationTypes.ASN:
        if (!this.userNotificationsForm.value['asnReceived']) {
          this.userNotificationsForm.controls.asnInterval.setValue(null);
          this.userNotificationsForm.get('asnInterval').disable();
        } else {
          this.userNotificationsForm.controls.asnInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('asnInterval').enable();
        }
        break;
      case NotificationTypes.CollectionBatch:
        if (!this.userNotificationsForm.value['collectionBatch']) {
          this.userNotificationsForm.controls.collectionBatchInterval.setValue(
            null
          );
          this.userNotificationsForm.get('collectionBatchInterval').disable();
        } else {
          this.userNotificationsForm.controls.collectionBatchInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('collectionBatchInterval').enable();
        }
        break;
      case NotificationTypes.InvoiceKeptOnHold:
        if (!this.userNotificationsForm.value['invoiceKeptOnHold']) {
          this.userNotificationsForm.controls.invoiceKeptOnHoldInterval.setValue(
            null
          );
          this.userNotificationsForm.get('invoiceKeptOnHoldInterval').disable();
        } else {
          this.userNotificationsForm.controls.invoiceKeptOnHoldInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('invoiceKeptOnHoldInterval').enable();
        }
        break;
      case NotificationTypes.InvoiceMissing:
        if (!this.userNotificationsForm.value['invoiceMissing']) {
          this.userNotificationsForm.controls.invoiceMissingInterval.setValue(
            null
          );
          this.userNotificationsForm.get('invoiceMissingInterval').disable();
        } else {
          this.userNotificationsForm.controls.invoiceMissingInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('invoiceMissingInterval').enable();
        }
        break;
      case NotificationTypes.RemittancesReceived:
        if (!this.userNotificationsForm.value['remittanceReceived']) {
          this.userNotificationsForm.controls.remittanceInterval.setValue(null);
          this.userNotificationsForm.get('remittanceInterval').disable();
        } else {
          this.userNotificationsForm.controls.remittanceInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('remittanceInterval').enable();
        }
        break;
      case NotificationTypes.ReceivedPAD:
        if (!this.userNotificationsForm.value['padReceived']) {
          this.userNotificationsForm.controls.padInterval.setValue(null);
          this.userNotificationsForm.get('padInterval').disable();
        } else {
          this.userNotificationsForm.controls.padInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('padInterval').enable();
        }
        break;
      case NotificationTypes.OS:
        if (!this.userNotificationsForm.value['osReceived']) {
          this.userNotificationsForm.controls.osInterval.setValue(null);
          this.userNotificationsForm.get('osInterval').disable();
        } else {
          this.userNotificationsForm.controls.osInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('osInterval').enable();
        }
        break;
      case NotificationTypes.POC:
        if (!this.userNotificationsForm.value['pocReceived']) {
          this.userNotificationsForm.controls.pocInterval.setValue(null);
          this.userNotificationsForm.get('pocInterval').disable();
        } else {
          this.userNotificationsForm.controls.pocInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('pocInterval').enable();
        }
        break;
      case NotificationTypes.AA:
        if (!this.userNotificationsForm.value['aaReceived']) {
          this.userNotificationsForm.controls.aaInterval.setValue(null);
          this.userNotificationsForm.get('aaInterval').disable();
        } else {
          this.userNotificationsForm.controls.aaInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('aaInterval').enable();
        }
        break;
      case NotificationTypes.TM:
        if (!this.userNotificationsForm.value['tmReceived']) {
          this.userNotificationsForm.controls.tmInterval.setValue(null);
          this.userNotificationsForm.get('tmInterval').disable();
        } else {
          this.userNotificationsForm.controls.tmInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('tmInterval').enable();
        }
        break;
      case NotificationTypes.STATEMENT:
        if (!this.userNotificationsForm.value['statementReceived']) {
          this.userNotificationsForm.controls.statementInterval.setValue(null);
          this.userNotificationsForm.get('statementInterval').disable();
        } else {
          this.userNotificationsForm.controls.statementInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('statementInterval').enable();
        }
        break;
      case NotificationTypes.REBATESTATEMENT:
        if (!this.userNotificationsForm.value['rebateStatementReceived']) {
          this.userNotificationsForm.controls.rebateStatementInterval.setValue(
            null
          );
          this.userNotificationsForm.get('rebateStatementInterval').disable();
        } else {
          this.userNotificationsForm.controls.rebateStatementInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('rebateStatementInterval').enable();
        }
        break;
      case NotificationTypes.CDA:
        if (!this.userNotificationsForm.value['cdaReceived']) {
          this.userNotificationsForm.controls.cdaInterval.setValue(null);
          this.userNotificationsForm.get('cdaInterval').disable();
        } else {
          this.userNotificationsForm.controls.cdaInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('cdaInterval').enable();
        }
        break;
      case NotificationTypes.DSDSI:
        if (!this.userNotificationsForm.value['dsdsiReceived']) {
          this.userNotificationsForm.controls.dsdsiInterval.setValue(null);
          this.userNotificationsForm.get('dsdsiInterval').disable();
        } else {
          this.userNotificationsForm.controls.dsdsiInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('dsdsiInterval').enable();
        }
        break;
      case NotificationTypes.PSRC:
        if (!this.userNotificationsForm.value['psrcReceived']) {
          this.userNotificationsForm.controls.psrcInterval.setValue(null);
          this.userNotificationsForm.get('psrcInterval').disable();
        } else {
          this.userNotificationsForm.controls.psrcInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('psrcInterval').enable();
        }
        break;
      case NotificationTypes.PI:
        if (!this.userNotificationsForm.value['piReceived']) {
          this.userNotificationsForm.controls.piInterval.setValue(null);
          this.userNotificationsForm.get('piInterval').disable();
        } else {
          this.userNotificationsForm.controls.piInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('piInterval').enable();
        }
        break;
      case NotificationTypes.WSO:
        if (!this.userNotificationsForm.value['wsoReceived']) {
          this.userNotificationsForm.controls.wsoInterval.setValue(null);
          this.userNotificationsForm.get('wsoInterval').disable();
        } else {
          this.userNotificationsForm.controls.wsoInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('wsoInterval').enable();
        }
        break;
      case NotificationTypes.WSTSA:
        if (!this.userNotificationsForm.value['wstsaReceived']) {
          this.userNotificationsForm.controls.wstsaInterval.setValue(null);
          this.userNotificationsForm.get('wstsaInterval').disable();
        } else {
          this.userNotificationsForm.controls.wstsaInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('wstsaInterval').enable();
        }
        break;
      case NotificationTypes.WSTRA:
        if (!this.userNotificationsForm.value['wstraReceived']) {
          this.userNotificationsForm.controls.wstraInterval.setValue(null);
          this.userNotificationsForm.get('wstraInterval').disable();
        } else {
          this.userNotificationsForm.controls.wstraInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('wstraInterval').enable();
        }
        break;
      case NotificationTypes.WSA:
        if (!this.userNotificationsForm.value['wsaReceived']) {
          this.userNotificationsForm.controls.wsaInterval.setValue(null);
          this.userNotificationsForm.get('wsaInterval').disable();
        } else {
          this.userNotificationsForm.controls.wsaInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('wsaInterval').enable();
        }
        break;
      case NotificationTypes.WIAA:
        if (!this.userNotificationsForm.value['wiaaReceived']) {
          this.userNotificationsForm.controls.wiaaInterval.setValue(null);
          this.userNotificationsForm.get('wiaaInterval').disable();
        } else {
          this.userNotificationsForm.controls.wiaaInterval.setValue(
            this.defaultInterval
          );
          this.userNotificationsForm.get('wiaaInterval').enable();
        }
        break;
    }
  }
  public requiredIfValidator(predicate) {
    return (formControl) => {
      if (!formControl.parent) {
        return null;
      }
      if (predicate()) {
        return Validators.required(formControl);
      }
      return null;
    };
  }
  public passwordFieldEmptyCheck(passwordStatus) {
    this.passwordEmpty = passwordStatus ? false : true;
  }

  public handleVerification(event: any) {
    try {
      const reducedUserForm = {};
      reducedUserForm['OTPRequired'] = event.desiredState;
      this.otpToggleEmitter.emit({
        ...reducedUserForm,
      });
    } catch {
      this.otpToggleResponse = {
        success: false,
        message: this.translocoService.translate('COMMON.UNKNOWN_ERROR'),
      };
    }
  }
  public handleNotificationsUpdate() {
    const request: INotificationRequest[] = [];
    this.userNotifications.map((ctl) => {
      let interval;
      let isActive;
      switch (ctl.notificationTypeKey) {
        case NotificationTypes.POA:
          interval = this.userNotificationsForm.get('poaInterval').value
            ? Number(this.userNotificationsForm.get('poaInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('poaReceived').value;
          break;
        case NotificationTypes.PO:
          interval = this.userNotificationsForm.get('poInterval').value
            ? Number(this.userNotificationsForm.get('poInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('poReceived').value;
          break;
        case NotificationTypes.Invoice:
          interval = this.userNotificationsForm.get('invoiceInterval').value
            ? Number(this.userNotificationsForm.get('invoiceInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('invoiceReceived').value;
          break;
        case NotificationTypes.ASN:
          interval = this.userNotificationsForm.get('asnInterval').value
            ? Number(this.userNotificationsForm.get('asnInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('asnReceived').value;
          break;
        case NotificationTypes.CollectionBatch:
          interval = this.userNotificationsForm.get('collectionBatchInterval')
            .value
            ? Number(
                this.userNotificationsForm.get('collectionBatchInterval').value
              )
            : 0;
          isActive = this.userNotificationsForm.get('collectionBatch').value;
          break;
        case NotificationTypes.InvoiceKeptOnHold:
          interval = this.userNotificationsForm.get('invoiceKeptOnHoldInterval')
            .value
            ? Number(
                this.userNotificationsForm.get('invoiceKeptOnHoldInterval')
                  .value
              )
            : 0;
          isActive = this.userNotificationsForm.get('invoiceKeptOnHold').value;
          break;
        case NotificationTypes.InvoiceMissing:
          interval = this.userNotificationsForm.get('invoiceMissingInterval')
            .value
            ? Number(
                this.userNotificationsForm.get('invoiceMissingInterval').value
              )
            : 0;
          isActive = this.userNotificationsForm.get('invoiceMissing').value;
          break;
        case NotificationTypes.RemittancesReceived:
          interval = this.userNotificationsForm.get('remittanceInterval').value
            ? Number(this.userNotificationsForm.get('remittanceInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('remittanceReceived').value;
          break;
        case NotificationTypes.ReceivedPAD:
          interval = this.userNotificationsForm.get('padInterval').value
            ? Number(this.userNotificationsForm.get('padInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('padReceived').value;
          break;
        case NotificationTypes.OS:
          interval = this.userNotificationsForm.get('osInterval').value
            ? Number(this.userNotificationsForm.get('osInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('osReceived').value;
          break;
        case NotificationTypes.POC:
          interval = this.userNotificationsForm.get('pocInterval').value
            ? Number(this.userNotificationsForm.get('pocInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('pocReceived').value;
          break;
        case NotificationTypes.AA:
          interval = this.userNotificationsForm.get('aaInterval').value
            ? Number(this.userNotificationsForm.get('aaInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('aaReceived').value;
          break;
        case NotificationTypes.TM:
          interval = this.userNotificationsForm.get('tmInterval').value
            ? Number(this.userNotificationsForm.get('tmInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('tmReceived').value;
          break;
        case NotificationTypes.STATEMENT:
          interval = this.userNotificationsForm.get('statementInterval').value
            ? Number(this.userNotificationsForm.get('statementInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('statementReceived').value;
          break;
        case NotificationTypes.REBATESTATEMENT:
          interval = this.userNotificationsForm.get('rebateStatementInterval')
            .value
            ? Number(
                this.userNotificationsForm.get('rebateStatementInterval').value
              )
            : 0;
          isActive = this.userNotificationsForm.get(
            'rebateStatementReceived'
          ).value;
          break;
        case NotificationTypes.CDA:
          interval = this.userNotificationsForm.get('cdaInterval').value
            ? Number(this.userNotificationsForm.get('cdaInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('cdaReceived').value;
          break;
        case NotificationTypes.DSDSI:
          interval = this.userNotificationsForm.get('dsdsiInterval').value
            ? Number(this.userNotificationsForm.get('dsdsiInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('dsdsiReceived').value;
          break;
        case NotificationTypes.PSRC:
          interval = this.userNotificationsForm.get('psrcInterval').value
            ? Number(this.userNotificationsForm.get('psrcInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('psrcReceived').value;
          break;
        case NotificationTypes.PI:
          interval = this.userNotificationsForm.get('piInterval').value
            ? Number(this.userNotificationsForm.get('piInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('piReceived').value;
          break;
        case NotificationTypes.WSO:
          interval = this.userNotificationsForm.get('wsoInterval').value
            ? Number(this.userNotificationsForm.get('wsoInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('wsoReceived').value;
          break;
        case NotificationTypes.WSTSA:
          interval = this.userNotificationsForm.get('wstsaInterval').value
            ? Number(this.userNotificationsForm.get('wstsaInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('wstsaReceived').value;
          break;
        case NotificationTypes.WSTRA:
          interval = this.userNotificationsForm.get('wstraInterval').value
            ? Number(this.userNotificationsForm.get('wstraInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('wstraReceived').value;
          break;
        case NotificationTypes.WSA:
          interval = this.userNotificationsForm.get('wsaInterval').value
            ? Number(this.userNotificationsForm.get('wsaInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('wsaReceived').value;
          break;
        case NotificationTypes.WIAA:
          interval = this.userNotificationsForm.get('wiaaInterval').value
            ? Number(this.userNotificationsForm.get('wiaaInterval').value)
            : 0;
          isActive = this.userNotificationsForm.get('wiaaReceived').value;
          break;
        default:
          break;
      }

      request.push({
        Interval: interval,
        IsActive: isActive,
        UserNotificationKey: ctl.userNotificationKey,
        UserKey: ctl.userKey,
        NotificationTypeKey: ctl.notificationTypeKey,
        AddedOn: ctl.addedOn,
        AddedBy: ctl.addedBy,
      });
    });

    this.updateUserNotifications.emit(request);
  }
  /*Handling Save Button Click */
  public handleSubmit() {
    try {
      this.updatedProfileInfo.emit({
        ...this.userProfileForm.value,
        ...this.phoneForm.value,
        phoneCountry: i18nIsoCountries.alpha2ToAlpha3(
          this.phoneForm.controls.phoneCountry.value || 'CA'
        ),
        phone: parsePhoneNumberWithError(
          this.phoneForm.controls.phone.value,
          this.phoneForm.controls.phoneCountry.value as CountryCode
        ).format('E.164'),
      });
    } catch {
      this.updatedProfileInfo.emit({
        ...this.userProfileForm.value,
        ...this.phoneForm.value,
        phoneCountry: this.defaultCountryCode || 'CAN',
        phone: '',
        phoneExtension: '',
      });
    }
  }

  // bound to Phone control's input event
  public formatPhoneNumber() {
    try {
      if (this.phoneForm.controls.phone.value.length < 1) {
        this.phoneForm.controls.phoneCountry.setValue(
          i18nIsoCountries.alpha3ToAlpha2(this.defaultCountryCode || 'CAN')
        );
      }
      this.phoneForm.controls.phone.setValue(
        parsePhoneNumberWithError(
          this.phoneForm.controls.phone.value,
          this.phoneForm.controls.phoneCountry.value
        ).formatInternational()
      );
    } catch {
      /*empty*/
    }
  }

  // bound to Countries Dropdown control's onChange event
  public resetPhoneNumber() {
    this.phoneForm?.controls?.phone?.setValue('');
  }

  // sets country code from 3 to 2 numbers
  private formatCountryCodeForDropdown() {
    try {
      return i18nIsoCountries.alpha3ToAlpha2(
        this.userProfileDetails.phoneCountry.trim()
      );
    } catch {
      return i18nIsoCountries.alpha3ToAlpha2(this.defaultCountryCode || 'CAN');
    }
  }

  // helper function for userProfileDetailsSetter
  private formatPhoneToInternational() {
    try {
      this.phoneNumber.input(this.userProfileDetails.phone);

      this.phoneForm.controls.phone.setValue(
        this.phoneNumber.getNumber().formatInternational()
      );
    } catch {
      this.phoneForm.controls.phone.setValue(
        this.userProfileDetails ? this.userProfileDetails.phone : ''
      );
    }
  }

  public validatePhoneForm(
    phoneNumberControlName: string,
    ExtensionControlName: string
  ): ValidatorFn {
    return (formGroup: FormGroup) => {
      try {
        const phoneNumberControl = formGroup.controls[phoneNumberControlName];
        const extensionControl = formGroup.controls[ExtensionControlName];
        return extensionControl &&
          extensionControl.value &&
          extensionControl.value.length > 0 &&
          phoneNumberControl.value.length === 0
          ? { invalidPhoneNumber: true }
          : null;
      } catch (error) {
        return error;
      }
    };
  }

  public validatePhoneNumber(
    parser: (text: string) => { country?: any; isValid(): boolean }
  ): ValidatorFn {
    return (control: AbstractControl) => {
      try {
        const phoneNumber = parser(control.value.toString());
        return phoneNumber?.isValid() &&
          (
            control.value.toString(),
            this.phoneForm?.controls?.phoneCountry?.value
          )
          ? null
          : {
              invalidPhoneNumber: true,
            };
      } catch {
        return control?.value?.length < 1
          ? null
          : {
              invalidPhoneNumber: true,
            };
      }
    };
  }
}
